import { getGroupingClassNames } from "@myaccount/_components/my-group-members/_client/get-grouping-class-names";
import { useI18n } from "@wojo/localization";
import { Table, Text } from "@wojo/ui";
import { ConfirmedRequestedGroupMemberTripsFragment } from "generated/graphql";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import React from "react";
import { useEmailSelection } from "@myaccount/_components/my-group-members/_client/hooks/use-email-selection";
import { EmailTravelersButton } from "../../email-travelers-button/EmailTravelersButton";
import { TravelerNameCheckbox } from "../../traveler-name-checkbox/TravelerNameCheckbox";
import styles from "./ContactInfoTable.module.scss";

export type ContactInfoProps = {
    bookedTravelers: ConfirmedRequestedGroupMemberTripsFragment[][];
    emailHelpTextId: string;
};

export const ContactInfoTable: React.FC<ContactInfoProps> = (props) => {
    const { bookedTravelers, emailHelpTextId } = props;
    const i18n = useI18n("account").account.myGroupMembers.contactInfoTable;
    const { selectedEmailsMap, allEmails, handleCheckboxChange } =
        useEmailSelection({
            confirmedTravelers: bookedTravelers,
        });
    const selectedEmails = Array.from(selectedEmailsMap.values()).filter(
        (email) => email,
    );
    const notApplicable = i18n.notApplicable.l();

    return (
        <>
            <div className={styles.table}>
                <Table
                    aria-describedby={emailHelpTextId}
                    header={
                        <Table.Header>
                            <Table.Cell className={styles["header-cell"]}>
                                {i18n.nameHeader.l()}
                            </Table.Cell>
                            <Table.Cell className={styles["offset-checkbox"]}>
                                {i18n.contactHeader.l()}
                            </Table.Cell>
                            <Table.Cell className={styles["offset-checkbox"]}>
                                {i18n.addressHeader.l()}
                            </Table.Cell>
                        </Table.Header>
                    }
                    showTitle={false}
                    tableTitle={
                        <Text.Heading
                            order="3"
                            style={{ paddingBottom: "var(--g-spacing-md)" }}
                        >
                            {i18n.tableTitle.l()}
                        </Text.Heading>
                    }
                    isStrictStyle={true}
                >
                    {bookedTravelers.map((group) =>
                        group.map((traveler, travelerIndex) => {
                            const email = traveler.customer?.email ?? "";
                            const phoneNumber =
                                traveler.customer?.phoneNumber ?? "";
                            const address = traveler.customer?.address;

                            const classNames = getGroupingClassNames(
                                travelerIndex,
                                group.length,
                            );
                            return (
                                <Table.Row
                                    key={traveler._id}
                                    className={classNames}
                                    header={
                                        <Table.Cell className={classNames}>
                                            <TravelerNameCheckbox
                                                id={traveler._id}
                                                email={email}
                                                selectedEmailsMap={
                                                    selectedEmailsMap
                                                }
                                                handleEmailCheckboxChange={
                                                    handleCheckboxChange
                                                }
                                                firstName={
                                                    traveler.customer
                                                        ?.firstName ?? ""
                                                }
                                                lastName={
                                                    traveler.customer
                                                        ?.lastName ?? ""
                                                }
                                            />
                                        </Table.Cell>
                                    }
                                >
                                    <Table.Cell>
                                        {!email && !phoneNumber ? (
                                            <>
                                                <span aria-hidden>—</span>
                                                <span className="visually-hidden">
                                                    {notApplicable}
                                                </span>
                                            </>
                                        ) : (
                                            <div className={styles.contact}>
                                                {email && (
                                                    <span>
                                                        <a
                                                            href={`mailto: ${email}`}
                                                        >
                                                            {email}
                                                        </a>
                                                    </span>
                                                )}
                                                {phoneNumber &&
                                                    isValidPhoneNumber(
                                                        phoneNumber,
                                                    ) && (
                                                        <span>
                                                            <a
                                                                href={`tel: ${phoneNumber}`}
                                                            >
                                                                {parsePhoneNumber(
                                                                    phoneNumber,
                                                                ).formatInternational()}
                                                            </a>
                                                        </span>
                                                    )}
                                            </div>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {!address ? (
                                            <>
                                                <span aria-hidden>—</span>
                                                <span className="visually-hidden">
                                                    {notApplicable}
                                                </span>
                                            </>
                                        ) : (
                                            <address className={styles.address}>
                                                <span>{address.street1}</span>
                                                <span>{address.street2}</span>
                                                <span>
                                                    {address.city}
                                                    {address.state && (
                                                        <>
                                                            {", "}
                                                            <span>
                                                                {address.state}{" "}
                                                            </span>
                                                            <span>
                                                                {
                                                                    address.postalCode
                                                                }
                                                            </span>
                                                        </>
                                                    )}
                                                </span>
                                            </address>
                                        )}
                                    </Table.Cell>
                                </Table.Row>
                            );
                        }),
                    )}
                </Table>
            </div>
            <EmailTravelersButton
                emails={selectedEmails}
                allEmails={allEmails}
            />
        </>
    );
};
